import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { useBotLearningQuery } from '../../hooks/queries';
import CadenceEditor from './CadenceEditor';
import { cadenceOfType } from './cadence';

// Ready-made schedules people start from (their text is in the viewer's language).
export const PRESETS = [
  { key: 'morning', cadence: { type: 'weekdays', time: '08:00' } },
  { key: 'overdue', cadence: { type: 'daily', time: '09:00' } },
  { key: 'weekly', cadence: { type: 'weekly', time: '16:00', days: [4] } },
];
export const fromPreset = (t, p) => ({
  title: t(`bots.schedules.presets.${p.key}_title`), instruction: t(`bots.schedules.presets.${p.key}_text`),
  cadence: p.cadence, skills: [],
});

const input = 'w-full text-sm rounded-lg border border-input bg-background px-2.5 py-1.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-amber-500/60';

/** Create or edit a schedule: what the bot does, when, and which skills it follows. */
const ScheduleForm = ({ open, onClose, onSave, busy, initial, projectId, timezone, editing = false, target = null }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => ({
    title: '', instruction: '', skills: [], ...(initial || {}), cadence: initial?.cadence || cadenceOfType('weekdays'),
  }));
  const { data: learning } = useBotLearningQuery(projectId, { enabled: open && !!projectId });
  const skills = (learning?.skills || []).filter((s) => !s.archived_at && s.status !== 'archived');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleSkill = (name) => setForm((f) => ({
    ...f, skills: f.skills.includes(name) ? f.skills.filter((x) => x !== name) : [...f.skills, name].slice(0, 5),
  }));
  const submit = (e) => {
    e.preventDefault();
    onSave({ title: form.title.trim(), instruction: form.instruction.trim(), cadence: form.cadence, skills: form.skills, timezone });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="space-y-3.5" data-testid="schedule-form">
          <DialogHeader>
            <DialogTitle>{t(editing ? 'bots.schedules.edit_title' : 'bots.schedules.dialog_title')}</DialogTitle>
            <DialogDescription>{t('bots.schedules.dialog_hint')}</DialogDescription>
          </DialogHeader>
          {target}
          {!editing && (
            <div className="flex flex-wrap gap-1.5" data-testid="schedule-presets">
              {PRESETS.map((p) => (
                <button key={p.key} type="button" onClick={() => setForm((f) => ({ ...f, ...fromPreset(t, p) }))}
                  className="px-2.5 py-1 text-xs rounded-full border border-input text-muted-foreground hover:text-foreground hover:border-amber-500/60">
                  {t(`bots.schedules.presets.${p.key}_title`)}
                </button>
              ))}
            </div>
          )}
          <label className="block space-y-1 text-sm">
            <span className="text-foreground">{t('bots.schedules.field_title')}</span>
            <input required maxLength={80} value={form.title} onChange={set('title')} className={input}
              placeholder={t('bots.schedules.field_title_placeholder')} />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-foreground">{t('bots.schedules.field_instruction')}</span>
            <textarea required rows={4} maxLength={4000} value={form.instruction} onChange={set('instruction')}
              className={input} placeholder={t('bots.schedules.field_instruction_placeholder')} />
          </label>
          <div className="space-y-1.5 text-sm">
            <span className="text-foreground">{t('bots.schedules.field_cadence')}</span>
            <CadenceEditor value={form.cadence} timezone={timezone} onChange={(cadence) => setForm((f) => ({ ...f, cadence }))} />
          </div>
          {skills.length > 0 && (
            <div className="space-y-1.5 text-sm">
              <span className="text-foreground">{t('bots.schedules.field_skills')}</span>
              <div className="flex flex-wrap gap-1">
                {skills.map((s) => {
                  const on = form.skills.includes(s.name);
                  return (
                    <button key={s.id} type="button" aria-pressed={on} onClick={() => toggleSkill(s.name)} title={s.description || ''}
                      className={`h-7 px-2.5 rounded-full border text-xs ${on
                        ? 'border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300'
                        : 'border-input text-muted-foreground hover:text-foreground'}`}>
                      {s.title || s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" className="bg-amber-500 text-honey-ink hover:bg-amber-400 font-semibold"
              disabled={busy || !projectId || !form.title.trim() || !form.instruction.trim()}>{t('bots.schedules.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleForm;
